import React, { useEffect, useMemo, useState, useCallback, Fragment } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { ClipLoader } from 'react-spinners';
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import {
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
  IdentificationIcon,
  MapPinIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

import { useAlert } from '../../context/AlertContext';
import {
  getEffectiveBookingStatus,
  isCancelledStatus,
} from '../../utils/bookingActionability';
import { mapBookingError } from '../../utils/bookingErrorMapper';

import {
  getEventBookings,
  approveBooking,
  rejectBooking,
} from '../../redux/actions/bookings';

import {
  cx,
  StatusPill,
  PaymentPill,
  formatEuro,
} from '../EventManagerModal/parts/atoms';

/**
 * Booking buckets:
 *   requests        — effective status === 'pending'
 *   pendingPayment  — Approved but not yet paid
 *   confirmed       — Paid (or Approved + paid)
 *   rejected        — Rejected
 *   cancelled       — Cancelled AND the payment moved (see below)
 *   expired         — effective status === 'expired'
 *   all             — everything
 */
const BUCKET_DEFS = [
  { key: 'all',            i18n: 'dashboard.em.bucket_all' },
  { key: 'requests',       i18n: 'dashboard.em.bucket_requests' },
  { key: 'pendingPayment', i18n: 'dashboard.em.bucket_pending_payment' },
  { key: 'confirmed',      i18n: 'dashboard.em.bucket_confirmed' },
  { key: 'rejected',       i18n: 'dashboard.em.bucket_rejected' },
  { key: 'cancelled',      i18n: 'dashboard.em.bucket_cancelled' },
  { key: 'expired',        i18n: 'dashboard.em.bucket_expired' },
];

// A brand can only cancel while unpaid (bookingCancellation.js
// CANCELLABLE_STATUSES = Pending|Approved, and never a paid booking), so a
// cancellation that touched money is an admin-issued manual refund. Those are
// the only ones the promoter needs: the rest are abandoned attempts.
const CANCELLED_PAYMENT_STATUSES = new Set(['paid', 'refunded']);

const isRefundedCancellation = (b) =>
  isCancelledStatus(getEffectiveBookingStatus(b)) &&
  CANCELLED_PAYMENT_STATUSES.has((b?.paymentStatus || '').toLowerCase());

// Buckets answering "where does this brand stand right now?" collapse to the
// brand's most recent booking — a brand that requested and cancelled twenty
// times before paying is one row, not twenty-one. The history buckets
// (rejected / cancelled / expired) are records and keep every matching row, so
// an issued refund never disappears because the brand booked again.
const DEDUPED_BUCKETS = new Set([
  'all',
  'requests',
  'pendingPayment',
  'confirmed',
]);

// Newest first, id as tie-breaker — same ordering the list itself uses.
const isNewerBooking = (a, b) => {
  const da = new Date(a?.createdAt || 0).getTime();
  const db = new Date(b?.createdAt || 0).getTime();
  if (da !== db) return da > db;
  return (a?.id || 0) > (b?.id || 0);
};

// Ids of each brand's most recent booking. Bookings with no resolvable brand
// are their own group so they are never swallowed by someone else's row.
const latestBookingIdPerBrand = (bookings) => {
  const latest = new Map();
  for (const b of bookings) {
    const brandId = b?.brandId ?? b?.Brand?.id ?? `booking:${b?.id}`;
    const prev = latest.get(brandId);
    if (!prev || isNewerBooking(b, prev)) latest.set(brandId, b);
  }
  return new Set([...latest.values()].map((b) => b?.id));
};

const bucketOf = (b) => {
  const status = getEffectiveBookingStatus(b);
  const pay = (b?.paymentStatus || '').toLowerCase();
  if (status === 'expired') return 'expired';
  if (status === 'pending') return 'requests';
  if (status === 'rejected') return 'rejected';
  if (isCancelledStatus(status)) return 'cancelled';
  if (status === 'paid') return 'confirmed';
  if (status === 'approved' && pay === 'paid') return 'confirmed';
  if (status === 'approved') return 'pendingPayment';
  return 'all';
};

const getBookingBillingInfo = (booking) =>
  booking?.billingInfo ||
  booking?.BillingInfo ||
  booking?.activePayment?.billingInfo ||
  booking?.Payment?.billingInfo ||
  null;

const isBookingPaid = (b) => {
  const eff = getEffectiveBookingStatus(b);
  if (eff === 'paid') return true;
  if (eff === 'approved' && (b?.paymentStatus || '').toLowerCase() === 'paid') {
    return true;
  }
  return false;
};

function BillingInfoModal({ open, onClose, billingInfo, brandName }) {
  const { t } = useTranslation();
  if (!open || !billingInfo) return null;

  return createPortal(
    <Transition show={open} as={Fragment}>
      <Dialog open={open} onClose={onClose} className="relative z-[1100]">
        <TransitionChild
          as={Fragment}
          enter="transition-opacity duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
        </TransitionChild>

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <TransitionChild
            as={Fragment}
            enter="transition-transform duration-200"
            enterFrom="scale-95 opacity-0"
            enterTo="scale-100 opacity-100"
            leave="transition-transform duration-150"
            leaveFrom="scale-100 opacity-100"
            leaveTo="scale-95 opacity-0"
          >
            <DialogPanel className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5">
              <button
                type="button"
                onClick={onClose}
                className="absolute top-4 right-4 rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition"
                aria-label={t('common.close', 'Close')}
              >
                <XMarkIcon className="w-5 h-5" />
              </button>

              <div className="flex items-start gap-3 border-b border-gray-100 px-6 py-5">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-brand-soft ring-1 ring-amber-100">
                  <IdentificationIcon className="h-5 w-5 text-amber-700" />
                </div>
                <div className="min-w-0 pr-8">
                  <DialogTitle className="text-base font-semibold text-gray-900">
                    {t('checkout.billing_manage_title')}
                  </DialogTitle>
                  {brandName && (
                    <p className="mt-0.5 truncate text-xs text-gray-500">
                      {brandName}
                    </p>
                  )}
                </div>
              </div>

              <dl className="divide-y divide-gray-100 px-6 py-2">
                {billingInfo.name && (
                  <div className="py-3">
                    <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                      {t('checkout.billing_name')}
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 break-words">
                      {billingInfo.name}
                    </dd>
                  </div>
                )}
                {billingInfo.nif && (
                  <div className="py-3">
                    <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                      {t('checkout.billing_nif')}
                    </dt>
                    <dd className="mt-1 font-mono text-sm text-gray-900 break-words">
                      {billingInfo.nif}
                    </dd>
                  </div>
                )}
                {billingInfo.address && (
                  <div className="py-3">
                    <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                      {t('checkout.billing_address')}
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 break-words">
                      {billingInfo.address}
                    </dd>
                  </div>
                )}
                {billingInfo.contact && (
                  <div className="py-3">
                    <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                      {t('checkout.billing_mobile')}
                    </dt>
                    <dd className="mt-1 font-mono text-sm text-gray-900 break-words">
                      {billingInfo.contact}
                    </dd>
                  </div>
                )}
              </dl>

              <div className="flex justify-end border-t border-gray-100 bg-gray-50 px-6 py-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-hover transition"
                >
                  {t('common.close', 'Close')}
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>,
    document.body
  );
}

// Shows the event floorplan with a highlighted marker on the spot a booking
// is requesting. Rendered as a plain <img> with a percent-positioned marker
// (placements store xPercent/yPercent as 0–1 fractions), so it needs no
// canvas and works even when the floorplan bucket lacks CORS headers.
function FloorplanSpotModal({
  open,
  onClose,
  floorplanUrl,
  placement,
  spaceName,
  brandName,
}) {
  const { t } = useTranslation();
  if (!open || !floorplanUrl || !placement) return null;

  const left = `${(placement.xPercent ?? 0) * 100}%`;
  const top = `${(placement.yPercent ?? 0) * 100}%`;

  return createPortal(
    <Transition show={open} as={Fragment}>
      <Dialog open={open} onClose={onClose} className="relative z-[1100]">
        <TransitionChild
          as={Fragment}
          enter="transition-opacity duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
        </TransitionChild>

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <TransitionChild
            as={Fragment}
            enter="transition-transform duration-200"
            enterFrom="scale-95 opacity-0"
            enterTo="scale-100 opacity-100"
            leave="transition-transform duration-150"
            leaveFrom="scale-100 opacity-100"
            leaveTo="scale-95 opacity-0"
          >
            <DialogPanel className="relative w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5">
              <button
                type="button"
                onClick={onClose}
                className="absolute top-4 right-4 rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition"
                aria-label={t('common.close', 'Close')}
              >
                <XMarkIcon className="w-5 h-5" />
              </button>

              <div className="flex items-start gap-3 border-b border-gray-100 px-6 py-5">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-brand-soft ring-1 ring-amber-100">
                  <MapPinIcon className="h-5 w-5 text-amber-700" />
                </div>
                <div className="min-w-0 pr-8">
                  <DialogTitle className="text-base font-semibold text-gray-900">
                    {t('dashboard.em.spot_modal_title', 'Requested spot')}
                  </DialogTitle>
                  <p className="mt-0.5 truncate text-xs text-gray-500">
                    {[brandName, spaceName].filter(Boolean).join(' · ')}
                  </p>
                </div>
              </div>

              <div className="max-h-[70vh] overflow-auto bg-gray-50 p-4">
                <div className="relative mx-auto w-fit">
                  <img
                    src={floorplanUrl}
                    alt={t('dashboard.floorplan_tab', 'Floorplan')}
                    className="block max-w-full rounded-lg ring-1 ring-black/10"
                  />
                  <span
                    className="absolute -translate-x-1/2 -translate-y-1/2"
                    style={{ left, top }}
                  >
                    <span className="absolute inset-0 -m-2 animate-ping rounded-full bg-brand/50" />
                    <span className="relative flex h-6 w-6 items-center justify-center rounded-full bg-brand ring-2 ring-gray-800 shadow-lg">
                      <MapPinIcon className="h-3.5 w-3.5 text-gray-900" />
                    </span>
                  </span>
                </div>
              </div>

              <div className="flex justify-end border-t border-gray-100 bg-gray-50 px-6 py-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-hover transition"
                >
                  {t('common.close', 'Close')}
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>,
    document.body
  );
}

const pillStatusFor = (b) => {
  const eff = getEffectiveBookingStatus(b);
  if (eff === 'paid') return 'Paid';
  if (eff === 'approved' && (b?.paymentStatus || '').toLowerCase() === 'paid') {
    return 'Paid';
  }
  if (eff === 'approved') return 'Approved';
  if (eff === 'pending') return 'Pending';
  if (eff === 'rejected') return 'Rejected';
  if (eff === 'expired') return 'Expired';
  if (isCancelledStatus(eff)) return 'Cancelled';
  return 'Open';
};

// Only cancellations carry a payment badge — everywhere else the booking status
// already says where the money stands.
const paymentPillFor = (b) => {
  if (!isRefundedCancellation(b)) return null;
  return (b?.paymentStatus || '').toLowerCase() === 'refunded'
    ? 'Refunded'
    : 'Paid';
};

function BookingRow({
  booking,
  activeKey,
  onApprove,
  onReject,
  onView,
  placement,
  floorplanUrl,
}) {
  const { t } = useTranslation();
  const [billingOpen, setBillingOpen] = useState(false);
  const [spotOpen, setSpotOpen] = useState(false);
  const isPending = getEffectiveBookingStatus(booking) === 'pending';
  const hasSpot = Boolean(placement && floorplanUrl);

  const brand = booking?.Brand || {};
  const brandName = brand.name || brand.username || t('common.unknown', 'Unknown');
  const brandAvatar = brand.avatar;
  const space = booking?.Space || booking?.space || {};
  const spaceName = space.name || space.id || '';
  const total = Number(booking?.totalAmount ?? space?.price ?? 0);
  const createdAt = booking?.createdAt ? moment(booking.createdAt).fromNow() : '';

  const billingInfo = getBookingBillingInfo(booking);
  const showBilling =
    Boolean(billingInfo) &&
    (isBookingPaid(booking) ||
      activeKey === 'confirmed' ||
      activeKey === 'all');

  return (
    <li className="group flex items-center gap-3 sm:gap-4 bg-white rounded-xl px-3 sm:px-4 py-3 ring-1 ring-black/5 hover:ring-black/10 hover:shadow-sm transition">
      {brandAvatar ? (
        <img
          src={brandAvatar}
          alt=""
          className="w-10 h-10 rounded-full ring-1 ring-gray-200 bg-gray-100 object-cover shrink-0"
        />
      ) : (
        <div className="w-10 h-10 rounded-full ring-1 ring-gray-200 bg-gray-100 shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-semibold text-gray-900 truncate">
            {brandName}
          </p>
          <StatusPill status={pillStatusFor(booking)} />
          <PaymentPill status={paymentPillFor(booking)} />
        </div>
        <p className="text-xs text-gray-500 mt-0.5 truncate">
          {[
            spaceName && `${t('dashboard.em.space', 'Space')} ${spaceName}`,
            total > 0 && formatEuro(total),
            createdAt && `${t('dashboard.em.requested', 'requested')} ${createdAt}`,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
      </div>
      {hasSpot && (
        <button
          type="button"
          onClick={() => setSpotOpen(true)}
          className="md:hidden p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 ring-1 ring-gray-200 shrink-0"
          title={t('dashboard.em.view_spot', 'View spot')}
          aria-label={t('dashboard.em.view_spot', 'View spot')}
        >
          <MapPinIcon className="w-4 h-4" />
        </button>
      )}
      <div className="hidden md:flex items-center gap-2 shrink-0">
        {hasSpot && (
          <button
            type="button"
            onClick={() => setSpotOpen(true)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-gray-700 hover:bg-gray-100 ring-1 ring-gray-200"
            title={t('dashboard.em.view_spot', 'View spot')}
            aria-label={t('dashboard.em.view_spot', 'View spot')}
          >
            <MapPinIcon className="w-4 h-4 text-gray-500" />
            <span className="hidden lg:inline">
              {t('dashboard.em.view_spot', 'View spot')}
            </span>
          </button>
        )}
        {showBilling && (
          <button
            type="button"
            onClick={() => setBillingOpen(true)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-gray-700 hover:bg-gray-100 ring-1 ring-gray-200"
            title={t('checkout.billing_manage_title')}
            aria-label={t('checkout.billing_manage_title')}
          >
            <IdentificationIcon className="w-4 h-4 text-gray-500" />
            <span className="hidden lg:inline">
              {t('checkout.billing_manage_title')}
            </span>
          </button>
        )}
        {isPending ? (
          <>
            <button
              type="button"
              onClick={onReject}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-600 hover:bg-gray-100 ring-1 ring-gray-200"
            >
              {t('dashboard.em.decline')}
            </button>
            <button
              type="button"
              onClick={onApprove}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-brand text-white hover:bg-brand-hover shadow-sm"
            >
              {t('dashboard.em.approve')}
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={onView}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-600 hover:bg-gray-100 ring-1 ring-gray-200"
          >
            {t('dashboard.em.view')}
          </button>
        )}
      </div>
      <BillingInfoModal
        open={billingOpen}
        onClose={() => setBillingOpen(false)}
        billingInfo={billingInfo}
        brandName={brandName}
      />
      <FloorplanSpotModal
        open={spotOpen}
        onClose={() => setSpotOpen(false)}
        floorplanUrl={floorplanUrl}
        placement={placement}
        spaceName={spaceName || placement?.spaceName}
        brandName={brandName}
      />
    </li>
  );
}

const BookingManagement = ({ eventId, eventSlug, event, onInvite }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showAlert } = useAlert();

  const { bookings = [], loading } = useSelector((s) => s.bookings || {});

  const [activeKey, setActiveKey] = useState('all');
  const [query, setQuery] = useState('');

  const floorplanUrl = event?.floorplanUrl || null;
  const placementById = useMemo(() => {
    const list = Array.isArray(event?.placements) ? event.placements : [];
    const map = new Map();
    for (const p of list) {
      if (p?.id) map.set(p.id, p);
    }
    return map;
  }, [event?.placements]);

  useEffect(() => {
    if (eventId) dispatch(getEventBookings(eventId));
  }, [dispatch, eventId]);

  useEffect(() => {
    if (!eventId) return undefined;
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        dispatch(getEventBookings(eventId));
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [dispatch, eventId]);

  const matchesQuery = useCallback(
    (b) => {
      if (!query) return true;
      const q = query.toLowerCase();
      const brand = b?.Brand?.name || b?.Brand?.username || '';
      const space = b?.Space?.name || '';
      const note = b?.additionalInfo || '';
      return (
        String(b?.id).includes(q) ||
        brand.toLowerCase().includes(q) ||
        space.toLowerCase().includes(q) ||
        note.toLowerCase().includes(q)
      );
    },
    [query]
  );

  const { buckets, counts } = useMemo(() => {
    const acc = {
      requests: [],
      pendingPayment: [],
      confirmed: [],
      rejected: [],
      cancelled: [],
      expired: [],
      all: [],
    };
    const currentIds = latestBookingIdPerBrand(bookings);
    for (const b of bookings) {
      const key = bucketOf(b);
      const isCurrent = currentIds.has(b?.id);
      if (key === 'cancelled') {
        if (isRefundedCancellation(b)) acc.cancelled.push(b);
      } else if (key !== 'all' && acc[key]) {
        if (!DEDUPED_BUCKETS.has(key) || isCurrent) acc[key].push(b);
      }
      // `all` is the catch-all, so unbucketed rows land here and nowhere else.
      if (isCurrent) acc.all.push(b);
    }
    const filtered = Object.fromEntries(
      Object.entries(acc).map(([k, arr]) => [k, arr.filter(matchesQuery)])
    );
    return {
      buckets: filtered,
      counts: Object.fromEntries(
        Object.entries(acc).map(([k, arr]) => [k, arr.length])
      ),
    };
  }, [bookings, matchesQuery]);

  const visible = useMemo(() => {
    const list = buckets[activeKey] || [];
    return [...list].sort((a, b) => {
      const da = new Date(a?.createdAt || 0).getTime();
      const db = new Date(b?.createdAt || 0).getTime();
      if (db !== da) return db - da;
      return (b?.id || 0) - (a?.id || 0);
    });
  }, [buckets, activeKey]);

  const refresh = useCallback(() => {
    if (eventId) dispatch(getEventBookings(eventId));
  }, [dispatch, eventId]);

  const showActionErrorAlert = useCallback(
    (action, message) => {
      showAlert({
        type: 'error',
        title: t(
          action === 'approve'
            ? 'alert_dialog.booking_actions.approve_error_title'
            : 'alert_dialog.booking_actions.reject_error_title'
        ),
        message,
        primaryAction: {
          label: t('alert_dialog.booking_actions.error_primary_action'),
        },
      });
    },
    [showAlert, t]
  );

  const handleApprove = async (bookingId) => {
    try {
      await dispatch(approveBooking(bookingId));
      showAlert({
        type: 'success',
        title: t('alert_dialog.booking_actions.approve_success_title'),
        message: t('alert_dialog.booking_actions.approve_success_message'),
        primaryAction: {
          label: t('alert_dialog.booking_actions.error_primary_action'),
        },
      });
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        'Failed to approve booking';
      showActionErrorAlert('approve', mapBookingError(msg, t));
    } finally {
      refresh();
    }
  };

  const handleReject = async (bookingId) => {
    try {
      await dispatch(rejectBooking(bookingId));
      showAlert({
        type: 'success',
        title: t('alert_dialog.booking_actions.reject_success_title'),
        message: t('alert_dialog.booking_actions.reject_success_message'),
        primaryAction: {
          label: t('alert_dialog.booking_actions.error_primary_action'),
        },
      });
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        'Failed to reject booking';
      showActionErrorAlert('reject', mapBookingError(msg, t));
    } finally {
      refresh();
    }
  };

  const handleViewBrand = (brandSlug) => {
    if (brandSlug) navigate(`/brands/${brandSlug}`);
  };

  const handleViewEvent = () => {
    if (eventSlug) navigate(`/event/${eventSlug}`);
  };

  const exportCsv = () => {
    if (!bookings.length) return;
    const header = [
      'id',
      'brand',
      'space',
      'status',
      'paymentStatus',
      'total',
      'createdAt',
    ];
    const rows = bookings.map((b) => [
      b?.id ?? '',
      (b?.Brand?.name || b?.Brand?.username || '').replace(/[",\n]/g, ' '),
      (b?.Space?.name || '').replace(/[",\n]/g, ' '),
      b?.status ?? '',
      b?.paymentStatus ?? '',
      b?.totalAmount ?? b?.Space?.price ?? '',
      b?.createdAt ?? '',
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map((v) => `"${String(v)}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bookings-${eventSlug || eventId || 'event'}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="w-full">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between mb-4">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('dashboard.em.search_bookings')}
            className="w-full pl-9 pr-3 py-2 rounded-lg ring-1 ring-gray-200 focus:ring-2 focus:ring-brand outline-none text-sm bg-white"
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={exportCsv}
            className="px-3 py-2 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-100 flex items-center gap-2"
          >
            <ArrowDownTrayIcon className="w-4 h-4" />
            {t('dashboard.em.export_csv')}
          </button>
          <button
            type="button"
            onClick={onInvite || handleViewEvent}
            className="px-3 py-2 rounded-lg bg-gray-900 text-white text-sm font-semibold hover:bg-black"
          >
            {t('dashboard.em.invite_brand')}
          </button>
        </div>
      </div>

      <div className="mb-4 overflow-x-auto em-nice-scroll">
        <div className="inline-flex gap-2 min-w-max py-1.5">
          {BUCKET_DEFS.map((b) => (
            <button
              key={b.key}
              type="button"
              onClick={() => setActiveKey(b.key)}
              className={cx(
                'px-3 py-2 rounded-full text-xs font-semibold transition whitespace-nowrap',
                activeKey === b.key
                  ? 'bg-brand text-white shadow-sm'
                  : 'bg-white ring-1 ring-gray-200 text-gray-600 hover:bg-gray-50'
              )}
            >
              <span className="flex items-center gap-1.5">
                {t(b.i18n)}
                <span
                  className={cx(
                    'px-1.5 py-0.5 rounded-full text-[10px]',
                    activeKey === b.key
                      ? 'bg-white/25 text-white'
                      : 'bg-gray-100 text-gray-500'
                  )}
                >
                  {counts[b.key] ?? 0}
                </span>
              </span>
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-40">
          <ClipLoader color="#FFBD59" size={30} />
        </div>
      ) : visible.length === 0 ? (
        <div className="text-center py-16 rounded-2xl bg-white ring-1 ring-black/5">
          <p className="text-sm text-gray-500">
            {t('dashboard.em.no_bookings_in_view')}
          </p>
        </div>
      ) : (
        <ul className="space-y-2">
          {visible.map((booking) => (
            <BookingRow
              key={booking.id}
              booking={booking}
              activeKey={activeKey}
              onApprove={() => handleApprove(booking.id)}
              onReject={() => handleReject(booking.id)}
              onView={() => handleViewBrand(booking?.Brand?.slug)}
              placement={
                booking?.placementId
                  ? placementById.get(booking.placementId) || null
                  : null
              }
              floorplanUrl={floorplanUrl}
            />
          ))}
        </ul>
      )}
    </div>
  );
};

export default BookingManagement;
